import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close, Print } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY     = '#4e342e';
const PRIMARY_MID = '#6d4c41';
const PRIMARY_LT  = '#efebe9';

const fmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};

const th  = { padding: '3px 8px', textAlign: 'left',  fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '2px solid rgba(62,39,35,0.2)' };
const thR = { ...th, textAlign: 'right' };
const td  = { padding: '3px 8px', fontSize: 11, color: '#374151', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };

const DetailedInvoiceReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [fetched, setFetched]     = useState(false);
  const [search, setSearch]       = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async (cId = companyId, bId = branchId, tok = token) => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    if (!cId || !bId || !tok) { setError('Authentication data missing'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetchData(
        'AccountsReportApi',
        `/DetailedInvoiceReport/${cId}/${bId}/${fromDate}/${toDate}`,
        tok
      );
      const data = res.response_data || res || [];
      setRows(Array.isArray(data) ? data : []);
      setFetched(true);
    } catch (err) {
      console.error('DetailedInvoiceReport error:', err);
      setError('Failed to fetch detailed invoice report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport(companyId, branchId, token);
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group rows by SaleId
  const invoices = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!map[r.SaleId]) {
        map[r.SaleId] = { header: r, items: [] };
      }
      if (r.ItemRowId) map[r.SaleId].items.push(r);
    });
    return Object.values(map);
  }, [rows]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.toLowerCase();
    return invoices.filter(inv =>
      (inv.header.InvoiceNo   || '').toLowerCase().includes(q) ||
      (inv.header.CustomerName|| '').toLowerCase().includes(q) ||
      (inv.header.GSTNumber   || '').toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const grandTotal = filtered.reduce((s, inv) => s + (parseFloat(inv.header.GrandTotal) || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'DetailedInvoiceReport', 0);
    if (!filtered.length) return;
    const sheetRows = [];

    filtered.forEach((inv, idx) => {
      const h = inv.header;
      sheetRows.push([`Invoice #${idx + 1}`, h.InvoiceNo, fmtDate(h.BillDate), h.CustomerName, h.ContactNumber, h.GSTNumber]);
      sheetRows.push(['Sl', 'Item', 'Qty', 'Rate', 'Disc%', 'Disc Amt', 'GST%', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total']);
      inv.items.forEach(it => {
        sheetRows.push([
          it.SlNo, it.ItemName, it.Quantity, it.Rate,
          it.DiscountPercentage, it.DiscountAmount,
          it.GSTRate, it.ItemTaxable,
          it.ItemCGST, it.ItemSGST, it.ItemIGST, it.ItemTotal,
        ]);
      });
      sheetRows.push(['', '', '', '', '', '', '', '', '', '', 'Grand Total', h.GrandTotal]);
      sheetRows.push([]);
    });

    await exportStyledExcel({
      reportTitle: 'DETAILED INVOICE REPORT',
      sheetName: 'Detailed Invoice',
      fileName: `DetailedInvoiceReport_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows: sheetRows,
    });
  };

  const handlePrint = () => window.print();

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading detailed invoice report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Detailed Invoice Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />

          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search invoice, customer..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={() => fetchReport()} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print" arrow>
            <IconButton onClick={handlePrint} disabled={!filtered.length} size="small" sx={iconBtn}>
              <Print sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Content */}
        <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 130px)', padding: '8px 0' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
              {fetched ? 'No records found' : 'Click refresh to load report'}
            </div>
          ) : (
            filtered.map((inv, idx) => {
              const h = inv.header;
              return (
                <div key={h.SaleId} style={{ margin: '0 12px 16px', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>

                  {/* Invoice Header */}
                  <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, color: '#fff', padding: '4px 10px', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 12 }}>#{idx + 1} &nbsp; {h.InvoiceNo}</span>
                    <span style={{ fontSize: 11, opacity: 0.85 }}>{fmtDate(h.BillDate)}</span>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>{h.CustomerName}</span>
                    {h.ContactNumber && <span style={{ fontSize: 11, opacity: 0.8 }}>📞 {h.ContactNumber}</span>}
                    {h.GSTNumber    && <span style={{ fontSize: 11, opacity: 0.8 }}>GST: {h.GSTNumber}</span>}
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>Grand Total: {fmt(h.GrandTotal)}</span>
                  </div>

                  {/* Summary row */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px', padding: '3px 10px', background: PRIMARY_LT, fontSize: 11, color: PRIMARY, borderBottom: '1px solid #d7ccc8' }}>
                    <span>Total: {fmt(h.TotalAmount)}</span>
                    <span>Bill Disc: {fmt(h.BillDiscount)}</span>
                    <span>Cash Disc: {fmt(h.CashDiscount)}</span>
                    <span>Taxable: {fmt(h.BillTaxable)}</span>
                    <span>CGST: {fmt(h.BillCGST)}</span>
                    <span>SGST: {fmt(h.BillSGST)}</span>
                    <span>IGST: {fmt(h.BillIGST)}</span>
                    <span>Round Off: {fmt(h.RoundOff)}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700 }}>
                      Cash: {fmt(h.CashAmount)} | Card: {fmt(h.CardAmount)} | UPI: {fmt(h.UPIAmount)} | Bal: {fmt(h.BalanceAmount)}
                    </span>
                  </div>

                  {/* Items table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: 'linear-gradient(135deg, #a1887f 0%, #bcaaa4 100%)', color: '#3e2723' }}>
                          <th style={th}>Sl</th>
                          <th style={th}>Item</th>
                          <th style={thR}>Qty</th>
                          <th style={thR}>Rate</th>
                          <th style={thR}>Disc%</th>
                          <th style={thR}>Disc Amt</th>
                          <th style={thR}>GST%</th>
                          <th style={thR}>Taxable</th>
                          <th style={thR}>CGST</th>
                          <th style={thR}>SGST</th>
                          <th style={thR}>IGST</th>
                          <th style={thR}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.items.length === 0 ? (
                          <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>No items</td></tr>
                        ) : (
                          inv.items.map((it, i) => (
                            <tr key={it.ItemRowId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ ...td, color: '#94a3b8' }}>{it.SlNo || i + 1}</td>
                              <td style={{ ...td, fontWeight: 600, color: '#1e293b', minWidth: 140 }}>{it.ItemName || '-'}</td>
                              <td style={tdR}>{it.Quantity}</td>
                              <td style={tdR}>{fmt(it.Rate)}</td>
                              <td style={tdR}>{it.DiscountPercentage}%</td>
                              <td style={tdR}>{fmt(it.DiscountAmount)}</td>
                              <td style={tdR}>{it.GSTRate}%</td>
                              <td style={tdR}>{fmt(it.ItemTaxable)}</td>
                              <td style={tdR}>{fmt(it.ItemCGST)}</td>
                              <td style={tdR}>{fmt(it.ItemSGST)}</td>
                              <td style={tdR}>{fmt(it.ItemIGST)}</td>
                              <td style={{ ...tdR, fontWeight: 700, color: PRIMARY }}>{fmt(it.ItemTotal)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}

          {/* Grand total footer */}
          {filtered.length > 0 && (
            <div style={{ margin: '0 12px 8px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, color: '#fff', borderRadius: 8, padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, fontWeight: 700 }}>
              <span>Total Invoices: {filtered.length}</span>
              <span>Grand Total: {fmt(grandTotal)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedInvoiceReport;
